import React from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'linaria';
import { headingSansSerif } from '@pocket/web-ui';
import { fontSansSerif, fontSizeRoot } from '@pocket/web-ui';
import { breakpointSmallTablet } from '@pocket/web-ui';
import { breakpointTinyTablet } from '@pocket/web-ui';
import { breakpointLargeHandset } from '@pocket/web-ui';
import { breakpointTinyHandset } from '@pocket/web-ui';
import { PageContainer } from '@pocket/web-ui';
import { FacebookMonoIcon, TwitterMonoIcon, InstagramMonoIcon } from '@pocket/web-ui';
import { Trans, useTranslation } from 'next-i18next';
import { Languages } from 'connectors/languages/languages';
import { PREMIUM_URL } from 'common/constants';
const appStoreBadge = 'https://assets.getpocket.com/web-ui/assets/apple-app-store-badge.2928664fe1fc6aca88583a6f606d60ba.svg';
const googlePlayBadge = 'https://assets.getpocket.com/web-ui/assets/google-play-badge.db9b21a1c41f3dcd9731e1e7acfdbb57.png';
const footerStyle = "fwtxdy0";
const footerPrimaryStyle = "f1jumjkx";
const footerPrimaryGroupStyle = "f4d74h";
const footerPrimaryAppButtonsStyle = "fkdonfk";
const appStoreBadgeStyle = "a1germmz";
const footerSecondaryStyle = "fsyu1m";
const legalLinksStyle = "l1ilsfnb";
const engagementBlockStyle = "egll6ip";
/**
 * The `GlobalFooter` component appears at the bottom of every screen in our web applications.
 */

export const GlobalFooter = ({
  device,
  hasBorder,
  hasColorBorder
}) => {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    language
  } = i18n;
  const languagePrefix = language === 'de' ? 'de' : 'en';
  const newsletterLink = `https://getpocket.com/${languagePrefix}/explore/pocket-hits-signup?src=footer_v2`;
  let appBadges = [<a href="https://apps.apple.com/us/app/pocket-save-read-grow/id309601447" className={appStoreBadgeStyle} key="footer-app-store-badge" target="_blank" rel="noopener noreferrer">
      <img src={appStoreBadge} alt={t('global-footer:app-store', 'Download On the Apple App Store')} />
    </a>, <a href="https://play.google.com/store/apps/details?id=com.ideashower.readitlater.pro" className={`${appStoreBadgeStyle} google-play-badge`} key="footer-google-play-badge" target="_blank" rel="noopener noreferrer">
      <img src={googlePlayBadge} alt={t('global-footer:google-play', 'Get It On Google Play')} />
    </a>]; // Only show the appropriate OS app store if we're on an iOS/Android device.
  // Defaults to showing both if we can't specifically identify iDevice/Android.

  if (device === 'ios') appBadges = appBadges.slice(0, 1);
  if (device === 'android') appBadges = appBadges.slice(1, 2);
  return <footer className={cx(footerStyle, hasBorder && 'with-border', hasColorBorder && 'with-color-border')}>
      <PageContainer className="footer-container">
        <div className={footerPrimaryStyle}>
          <div className={footerPrimaryGroupStyle}>
            <h6>{t('global-footer:products', 'Products')}</h6>
            <nav aria-label="Products">
              <ul>
                <li>
                  <a href="https://getpocket.com/explore?src=footer_v2">
                    {t('global-footer:must-read-articles', 'Must-read articles')}
                  </a>
                </li>
                <li>
                  <a href={newsletterLink}>
                    {t('global-footer:daily-newsletter', 'Daily newsletter')}
                  </a>
                </li>
                <li>
                  <a href={`${PREMIUM_URL}&utm_campaign=global-bottom&src=footer_v2`}>
                    {t('global-footer:pocket-premium', 'Pocket Premium')}
                  </a>
                </li>

                <li>
                  <a href="https://getpocket.com/welcome?src=footer_v2">
                    {t('global-footer:save-to-pocket-extensions', 'Save to Pocket extensions')}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
          <div className={footerPrimaryGroupStyle}>
            <h6>{t('global-footer:company', 'Company')}</h6>
            <nav aria-label="Company">
              <ul>
                <li>
                  <a href="https://getpocket.com/about?src=footer_v2">
                    {t('global-footer:about', 'About')}
                  </a>
                </li>
                <li>
                  <a href="https://getpocket.com/jobs?src=footer_v2">
                    {t('global-footer:careers', 'Careers')}
                  </a>
                </li>
                <li>
                  <a href="https://blog.getpocket.com?src=footer_v2">
                    {t('global-footer:blog', 'Blog')}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
          <div className={footerPrimaryGroupStyle}>
            <h6>{t('global-footer:resources', 'Resources')}</h6>
            <nav aria-label="Resources">
              <ul>
                <li>
                  <a href="https://help.getpocket.com/?src=footer_v2">
                    {t('global-footer:get-help', 'Get help')}
                  </a>
                </li>
                <li>
                  <a href="https://getpocket.com/developer?src=footer_v2">
                    {t('global-footer:developer-api', 'Developer API')}
                  </a>
                </li>
                <li>
                  <a href="https://getpocket.com/publisher?src=footer_v2">
                    {t('global-footer:pocket-for-publishers', 'Pocket for publishers')}
                  </a>
                </li>

                <li>
                  <a href="https://getpocket.com/sponsor?src=footer_v2">
                    {t('global-footer:advertise', 'Advertise')}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
          <div className={cx(footerPrimaryGroupStyle, footerPrimaryAppButtonsStyle)}>
            <h6>{t('global-footer:get-the-app', 'Get the app')}</h6>
            {appBadges}
          </div>
        </div>

        <div className={footerSecondaryStyle}>
          <div className={legalLinksStyle}>
            <span>
              <Trans i18nKey="global-footer:pocket-is-part-of">
                Pocket is part of the{' '}
                <a href="https://www.mozilla.org/about/" target="_blank" rel="noopener noreferrer">
                  Mozilla
                </a>{' '}
                family of products.
              </Trans>
            </span>
            <nav aria-label="Legal">
              <span>
                &copy; {new Date().getFullYear()}{' '}
                {t('global-footer:read-it-later-inc', 'Read It Later, Inc.')}
              </span>
              <a href="https://getpocket.com/privacy?src=footer_v2">
                {t('global-footer:privacy-policy', 'Privacy policy')}
              </a>
              <a href="https://getpocket.com/tos?src=footer_v2">
                {t('global-footer:terms-of-service', 'Terms of service')}
              </a>
              <button id="ot-sdk-btn" className="ot-sdk-show-settings">
                {t('global-footer:cookie-preferences', 'Cookie preferences')}
              </button>
            </nav>
          </div>
          <div className={engagementBlockStyle}>
            <div className="languages-wrapper">
              {t('global-footer:language', 'Language')}
              <div className="languages">
                <Languages />
              </div>
            </div>
            <div className="social-wrapper">
              {t('global-footer:follow-pocket', 'Follow Pocket')}
              <div className="social">
                <a href="https://facebook.com/pocket" target="_blank" rel="noopener noreferrer">
                  <FacebookMonoIcon id="facebook-footer-icon" title="Facebook" description={t('global-footer:visit-facebook', 'Visit our Facebook page')} />
                </a>
                <a href="https://twitter.com/pocket" target="_blank" rel="noopener noreferrer">
                  <TwitterMonoIcon id="twitter-footer-icon" title="Twitter" description={t('global-footer:view-twitter', 'View our Twitter profile')} />
                </a>
                <a href="https://instagram.com/pocket" target="_blank" rel="noopener noreferrer">
                  <InstagramMonoIcon id="instagram-footer-icon" title="Instagram" description={t('global-footer:view-instagram', 'Find us on Instagram')} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </PageContainer>
    </footer>;
};
GlobalFooter.propTypes = {
  device: PropTypes.string,

  /**
   * Defaults to true - provides the footer with a full-width top border.
   */
  hasBorder: PropTypes.bool,

  /**
   * Defaults to false - provides the footer with a full-width colored border. Overrides `hasBorder` styles
   */
  hasColorBorder: PropTypes.bool
};
GlobalFooter.defaultProps = {
  hasBorder: true,
  hasColorBorder: false
};

require("./global-footer.linaria.module.css!=!../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./global-footer.js");